import { useMemo, useState } from "react";
import { addMonths, endOfMonth, startOfMonth } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../../client/components/ui/button";
import useTranslation from "../../../client/hooks/useTranslation";
import { useLiteData, type Charge } from "../../data/LiteDataProvider";
import { Card, Pnl } from "../../ui/ui";
import { breakdown, kpis, roiOf, type PfmAccount } from "./pfmModel";
import { RoiText } from "./shared";

/** Récap d'un mois calendaire : mêmes règles que les KPIs (computePfmKpis sur le mois). */
export function MonthRecap({ accounts, charges, className = "" }: { accounts: PfmAccount[]; charges: Charge[]; className?: string }) {
  const t = useTranslation().lite;
  const p = t.pfm.recap;
  const { fmt } = useLiteData();
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));

  const { b, net } = useMemo(() => {
    const end = endOfMonth(cursor);
    return { b: breakdown(accounts, charges, cursor, end), net: kpis(accounts, charges, cursor, end).netProfit };
  }, [accounts, charges, cursor]);

  const money = (v: number) => (v ? fmt.money(-v) : "-");
  const rows: { label: string; value: number; hide?: boolean }[] = [
    { label: p.challenges, value: b.challenges },
    { label: p.activations, value: b.activations },
    { label: p.recurring, value: b.recurring },
    { label: p.oneOff, value: b.oneOff, hide: !b.oneOff },
  ];

  return (
    <Card
      className={className}
      title={p.title}
      action={
        <div className="flex items-center">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setCursor(c => addMonths(c, -1))} aria-label={t.common.previousMonth}><ChevronLeft size={14} /></Button>
          <span className="text-xs w-28 text-center">{fmt.monthYear(cursor)}</span>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setCursor(c => addMonths(c, 1))} aria-label={t.common.nextMonth}><ChevronRight size={14} /></Button>
        </div>
      }
    >
      <div className="space-y-2.5 text-sm">
        {rows.filter(r => !r.hide).map(r => (
          <div key={r.label} className="flex justify-between gap-3">
            <span className="text-muted-foreground">{r.label}</span>
            <span className={`font-mono tabular-nums ${r.value ? "text-pnlNegative" : "text-muted-foreground/70"}`}>{money(r.value)}</span>
          </div>
        ))}
        <div className="flex justify-between gap-3">
          <span className="text-muted-foreground">{p.payouts}</span>
          <span className={`font-mono tabular-nums ${b.payouts ? "text-pnlPositive" : "text-muted-foreground/70"}`}>{b.payouts ? fmt.money(b.payouts) : "-"}</span>
        </div>
        <div className="flex justify-between gap-3 pt-2.5 border-t border-border">
          <span className="text-muted-foreground">{p.totalExpenses}</span>
          <span className={`font-mono tabular-nums ${b.total ? "text-pnlNegative" : "text-muted-foreground/70"}`}>{money(b.total)}</span>
        </div>
        <div className="flex justify-between gap-3 font-medium pt-1">
          <span>{p.net}</span>
          <Pnl value={net} className="font-semibold" />
        </div>
        <div className="flex justify-between gap-3">
          <span className="text-muted-foreground">{p.roi}</span>
          <RoiText roi={roiOf(b.payouts, b.total)} />
        </div>
      </div>
    </Card>
  );
}
